import { Component, OnInit, HostListener, ViewChild, ElementRef, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SelectClientPopUpComponent } from '../select-client-pop-up/select-client-pop-up.component';
import { SmartRefundFinderComponent } from '../smart-refund-finder/smart-refund-finder.component';
import { StateService } from '../service/state.service';
import { Client } from '../Module/Client.model';
import { Router, NavigationEnd } from '@angular/router';
import { ClientService } from '../service/client.service';
import { AuthService } from '../service/auth.service';
import { AuthStateService } from '../service/auth-state.service';
import { WebSocketService, Notification } from '../service/websocket.service';
import { NotificationService, Notification as HttpNotification } from '../service/notification.service';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { CashDiscrepancyService } from '../service/cash-discrepancy.service';
import { StockDiscrepancyService } from '../service/stock-discrepancy.service';
import { NotificationRefreshService } from '../service/notification-refresh.service';
import { CashJournalService, CashOpenType, CashOpenOrigin } from '../service/cash-journal.service';
import { HardwareService } from '../service/hardware.service';
import { environment } from '../../environments/environment';
import { PushNotificationService } from '../service/push-notification.service';
import { AvoirService } from '../service/avoir.service';
import { BarcodeScannerService } from '../service/barcode-scanner.service';


@Component({
  selector: 'app-nav-bar',
  templateUrl: './nav-bar.component.html',
  styleUrls: ['./nav-bar.component.css']
})
export class NavBarComponent implements OnInit, OnDestroy {
  @ViewChild('searchInput') searchInput!: ElementRef;
  @ViewChild('notificationRef') notificationRef!: ElementRef;
  @ViewChild('searchRef') searchRef!: ElementRef;
  @ViewChild('profileRef') profileRef!: ElementRef;

  searchTerm: string = '';
  clients: Client[] = [];
  filteredClients: Client[] = [];
  showDropdown: boolean = false;
  isNavCollapsed: boolean = true;
  isLoading: boolean = false;
  isAuthenticated: boolean = false;
  notifications: Notification[] = [];
  unreadCount: number = 0;
  showNotifications: boolean = false;
  isAdmin: boolean = false;
  private destroy$ = new Subject<void>();

  // Pagination state
  private pageSize = 10;
  private offset = 0;
  private loadingMore = false;
  private allLoaded = false;

  // Facebook-style notification tab state
  notificationTab: 'all' | 'unread' = 'all';

  // Loading states for approve/reject buttons
  approvingNotifications: Set<string> = new Set();
  rejectingNotifications: Set<string> = new Set();
  hasNewNotification: boolean = false;
  latestNotification: Notification | null = null;
  showNotificationMessage: boolean = false;

  // Timer for refreshing timestamps
  private timestampRefreshTimer: any;

  // Window focus listener reference
  private windowFocusListener: (() => void) | null = null;

  // New dropdown and mobile menu state
  activeDropdown: string | null = null;
  isMobileMenuOpen: boolean = false;
  activeMobileDropdown: string | null = null;
  showMobileDropdown: boolean = false;
  notificationPermissionStatus: NotificationPermission = 'default';

  setNotificationTab(tab: 'all' | 'unread') {
    this.notificationTab = tab;
    // Force change detection to update the filtered notifications
    this.cdr.detectChanges();
  }

  seeAllNotifications() {
    // Implement navigation or modal for all notifications
    // For now, just log
    console.log('See all notifications clicked');
  }

  filteredNotifications() {
    return this.notificationTab === 'all'
      ? this.notifications
      : this.notifications.filter(n => !n.read);
  }

  // New dropdown methods
  toggleDropdown(dropdownName: string): void {
    this.activeDropdown = this.activeDropdown === dropdownName ? null : dropdownName;
  }

  closeDropdown(): void {
    this.activeDropdown = null;
  }

  // Mobile menu methods
  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    if (this.isMobileMenuOpen) {
      this.loadUserData();
    }
    this.cdr.detectChanges();
  }

  closeMobileMenu(): void {
    // Ne pas fermer le menu si la recherche mobile est active
    if (this.showMobileDropdown && this.filteredClients.length > 0) {
      return;
    }

    this.isMobileMenuOpen = false;
    this.activeMobileDropdown = null;
    this.cdr.detectChanges();
  }

  toggleMobileDropdown(dropdownName: string): void {
    this.activeMobileDropdown = this.activeMobileDropdown === dropdownName ? null : dropdownName;
  }

  // User information methods
  getUserInitials(): string {
    const user = this.authService.getUser();
    if (user && user.firstName && user.lastName) {
      return user.firstName.charAt(0) + user.lastName.charAt(0);
    }
    return 'U';
  }

  getUserName(): string {
    const user = this.authService.getUser();
    if (user && user.firstName && user.lastName) {
      return user.firstName + ' ' + user.lastName;
    }
    return 'Utilisateur';
  }

  getUserEmail(): string {
    const user = this.authService.getUser();
    return user?.email || 'email@example.com';
  }

  getUserRole(): string {
    const user = this.authService.getUser();
    return user?.role?.toLowerCase() || 'user';
  }

  // Method to check if user data is available and valid
  isUserDataValid(): boolean {
    const user = this.authService.getUser();

    if (!user) {
      return false;
    }

    const hasUsername = !!user.username;
    const hasFirstName = !!user.firstName;
    const hasEmail = !!user.email;

    const isValid = hasUsername && (hasFirstName || hasEmail);

    return isValid;
  }

  constructor(
    private clientService: ClientService,
    private dialog: MatDialog,
    private stateService: StateService,
    private router: Router,
    private authService: AuthService,
    private authStateService: AuthStateService,
    private webSocketService: WebSocketService,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
    private cashDiscrepancyService: CashDiscrepancyService,
    private stockDiscrepancyService: StockDiscrepancyService,
    private notificationRefreshService: NotificationRefreshService,
    private hardwareService: HardwareService,
    private pushNotificationService: PushNotificationService,
    private avoirService: AvoirService,
    public barcodeScannerService: BarcodeScannerService
  ) {

    // Subscribe to router events to close navbar on navigation
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.closeNavbar();
    });
  }

  ngOnInit(): void {
    // Initialize user data immediately
    this.initializeUserData();

    // Set up periodic refresh of user data
    this.setupUserDataRefresh();

    // Initialize responsive debugging
    this.initializeResponsiveDebug();

    // Subscribe to notification refresh events
    this.notificationRefreshService.refresh$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loadNotificationsPage(true);
      });

    // Subscribe to auth state changes to handle initialization dynamically
    this.subscribeToAuthState();

    // Start timer to refresh timestamps every minute
    // Only if authenticated? No, handle inside logic. 
    // Actually the timer can run, it's harmless.
    this.startTimestampRefreshTimer();

    // Refresh user data every 30 seconds to ensure it stays current
    setInterval(() => {
      if (this.authService.isAuthenticated()) {
        this.refreshUserData();
      }
    }, 30000);

    // Check initial notification permission
    this.checkNotificationPermission();

    // Auto-refresh subscription if already permission is granted (handles key rotation)
    if (this.notificationPermissionStatus === 'granted') {
      this.pushNotificationService.initializePushNotifications();
    }
  }

  checkNotificationPermission(): void {
    this.notificationPermissionStatus = this.pushNotificationService.getPermissionStatus();
    this.cdr.detectChanges();
  }

  async enableNotifications(): Promise<void> {
    try {
      this.notificationService.info('Demande d\'autorisation en cours...');
      const success = await this.pushNotificationService.initializePushNotifications();

      this.checkNotificationPermission();

      if (success) {
        this.notificationService.success('Notifications mobiles activées avec succès !');
      } else {
        if (this.notificationPermissionStatus === 'denied') {
          this.notificationService.error('Notifications bloquées. Veuillez les activer dans les paramètres du navigateur (cadenas 🔒).');
        } else {
          this.notificationService.warning('Impossible d\'activer les notifications. Vérifiez que vous êtes en HTTPS ou Localhost.');
        }
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
      this.notificationService.error('Erreur technique lors de l\'activation des notifications');
    }
  }

  async testNotification(): Promise<void> {
    try {
      this.notificationService.info('Envoi du test...');
      const success = await this.pushNotificationService.sendTestNotification();
      if (success) {
        this.notificationService.success('Test envoyé ! Vérifiez votre téléphone 📱');
      } else {
        this.notificationService.error('Erreur lors de l\'envoi du test');
      }
    } catch (e) {
      console.error(e);
    }
  }

  // Initialize user data and ensure it's properly loaded
  private initializeUserData(): void {
    // Force refresh user data
    this.forceRefreshUserData();

    // Check if user data is valid
    if (!this.isUserDataValid()) {
      this.loadUserData();
    }
  }

  // Set up periodic refresh of user data
  private setupUserDataRefresh(): void {
    // Refresh user data every 5 seconds for the first 30 seconds to ensure it loads
    let refreshCount = 0;
    const initialRefreshInterval = setInterval(() => {
      if (this.authService.isAuthenticated() && !this.isUserDataValid()) {
        this.forceRefreshUserData();
        refreshCount++;

        if (refreshCount >= 6) { // 6 attempts = 30 seconds
          clearInterval(initialRefreshInterval);
        }
      } else if (this.isUserDataValid()) {
        clearInterval(initialRefreshInterval);
      }
    }, 5000); // Every 5 seconds
  }

  // Initialize responsive debugging and event listeners
  private initializeResponsiveDebug(): void {
    this.loadUserData();
    this.subscribeToAuthState();

    // Add window focus listener to refresh user data when tab becomes active
    this.windowFocusListener = () => {
      if (this.authService.isAuthenticated()) {
        this.refreshUserData();
      }
    };
    window.addEventListener('focus', this.windowFocusListener);

    // Add visibility change listener to refresh user data when tab becomes visible
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && this.authService.isAuthenticated()) {
        this.refreshUserData();
      }
    });

    // Add page focus listener to refresh user data when page gains focus
    window.addEventListener('pageshow', () => {
      if (this.authService.isAuthenticated()) {
        this.refreshUserData();
      }
    });

    // Add window resize listener for responsive debugging
    window.addEventListener('resize', () => {
      this.handleWindowResize();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    // Clear the timestamp refresh timer
    if (this.timestampRefreshTimer) {
      clearInterval(this.timestampRefreshTimer);
    }

    // Remove window focus listener
    if (this.windowFocusListener) {
      window.removeEventListener('focus', this.windowFocusListener);
    }
  }

  subscribeToAuthState(): void {
    this.authStateService.isAuthenticated$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isAuthenticated => {
        this.isAuthenticated = isAuthenticated;
        if (isAuthenticated) {
          this.checkUserRole();
          this.loadUserData();
          this.loadClients();

          // Re-subscribe to notifications to ensure connection
          this.subscribeToNotifications();
          this.loadNotificationsPage(true);
        }
      });
  }

  private checkUserRole(): void {
    const user = this.authService.getUser();
    const isAdmin = user?.role === 'ADMIN';
    this.isAdmin = isAdmin;
  }

  loadUserData(): void {
    // Force change detection to update the UI with user data
    this.cdr.detectChanges();

    // Also trigger change detection after a short delay to ensure UI updates
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 100);
  }

  // Method to refresh user data when component becomes visible
  refreshUserData(): void {
    this.loadUserData();
    this.checkUserRole();
    this.forceRefreshUserData();

    // Refresh WebSocket subscription for admin notifications
    this.webSocketService.refreshAdminSubscription();

    // If user data is not valid, try to reload it
    if (!this.isUserDataValid() && this.authService.isAuthenticated()) {
      // Force a re-check of authentication state
      this.authStateService.setAuthenticated(true);
    }
  }

  // Method to force refresh user data from storage
  forceRefreshUserData(): void {
    // Force change detection multiple times to ensure UI updates
    this.cdr.detectChanges();
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 50);
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 100);
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 200);
  }



  loadClients(): void {
    this.isLoading = true;
    this.clientService.listClients().subscribe({
      next: (data) => {
        this.clients = data;
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
      }
    });
  }

  normalize(text: string): string {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  onSearchChange(): void {
    this.filterClients();
  }

  onInputFocus(): void {
    this.showDropdown = true;
    this.filterClients();
  }

  onMobileSearchFocus(event: Event): void {
    event.stopPropagation(); // Empêche la propagation de l'événement
    this.showMobileDropdown = true;
    this.filterClients();
    // Empêche la fermeture du menu mobile
    if (this.isMobileMenuOpen) {
      setTimeout(() => {
        this.isMobileMenuOpen = true;
        this.cdr.detectChanges();
      }, 0);
    }
  }

  filterClients(): void {
    if (!this.searchTerm.trim()) {
      this.filteredClients = this.clients.slice(0, 10);
      return;
    }

    const normalizedSearch = this.normalize(this.searchTerm);
    const searchTokens = normalizedSearch.split(' ');

    this.filteredClients = this.clients
      .filter(client => {
        const combined = this.normalize(
          `${client.firstName} ${client.lastName} ${client.email || ''} ${client.phoneNumber || ''}`
        );
        return searchTokens.every(token => combined.includes(token));
      })
      .slice(0, 10);
  }

  hideDropdown(): void {
    setTimeout(() => {
      this.showDropdown = false;
    }, 200);
  }

  openClientFormDialog(target: 'addOrder' | 'addCashLoan'): void {
    this.closeNavbar();
    this.stateService.setTargetRoute(target);
    this.dialog.open(SelectClientPopUpComponent);
  }

  openSmartRefundFinder(): void {
    this.closeNavbar();
    this.dialog.open(SmartRefundFinderComponent, {
      width: '800px',
      disableClose: false
    });
  }

  toggleNavbar(): void {
    this.isNavCollapsed = !this.isNavCollapsed;
  }

  closeNavbar(): void {
    this.isNavCollapsed = true;
    this.activeDropdown = null;
    this.closeMobileMenu();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.showDropdown = false;
    this.showMobileDropdown = false;
    this.filteredClients = [];
    if (this.searchInput) {
      this.searchInput.nativeElement.blur();
    }
  }

  // Close dropdown when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const clickedElement = event.target as HTMLElement;

    // Close search dropdown - check for both desktop and mobile search containers
    if (!clickedElement.closest('.search-container') &&
      !clickedElement.closest('input[type="text"]') &&
      !clickedElement.closest('.lg\\:hidden input[type="text"]')) {
      this.showDropdown = false;
      this.showMobileDropdown = false;
    }

    // Close notification dropdown
    if (!clickedElement.closest('.notifications-container')) {
      this.showNotifications = false;
    }

    // Close desktop dropdowns
    if (!clickedElement.closest('.relative')) {
      this.activeDropdown = null;
    }

    // Close mobile menu
    if (!clickedElement.closest('.lg\\:hidden') && !clickedElement.closest('button')) {
      this.isMobileMenuOpen = false;
    }
  }

  logout(): void {
    const isRememberMe = this.authService.isRememberMeSession();

    this.authService.logout().subscribe({
      next: () => {
        // Successfully logged out from backend

        this.router.navigate(['/login']);
      },
      error: (error) => {
        // Even if backend logout fails, redirect to login
        console.error('❌ [NAV-BAR] Logout error:', error);
        this.router.navigate(['/login']);
      }
    });
  }

  toggleNotifications(): void {
    this.showNotifications = !this.showNotifications;
    if (this.showNotifications) {
      // Always refresh notifications when opening the dropdown to get latest read status
      this.loadNotificationsPage(true);

      // Reset new notification flag when user opens dropdown
      this.hasNewNotification = false;
      this.latestNotification = null;

      // Force refresh timestamps when opening dropdown
      setTimeout(() => {
        this.cdr.detectChanges();
      }, 100);
    }
    // Prevent event bubbling
    event?.stopPropagation();
  }

  loadNotificationsPage(reset: boolean = false): void {
    if (this.loadingMore) return;
    if (!reset && this.allLoaded) return;
    this.loadingMore = true;
    if (reset) {
      this.offset = 0;
      this.allLoaded = false;
      this.notifications = [];
    }

    this.notificationService.getNotificationsPage(this.pageSize, this.offset).subscribe({
      next: (page) => {
        const newNotifs: Notification[] = page.content.map((n: HttpNotification) => {
          const processedTimestamp = this.parseTimestamp(n.timestamp);

          // Parse notification data if it's a string
          let processedData = n.data;
          if (typeof n.data === 'string' && n.data) {
            try {
              processedData = JSON.parse(n.data);
            } catch (error) {
              processedData = null;
            }
          }

          return {
            ...n,
            timestamp: processedTimestamp,
            data: processedData
          };
        });
        if (reset) {
          this.notifications = newNotifs;
        } else {
          this.notifications = [...this.notifications, ...newNotifs];
        }
        if (newNotifs.length < this.pageSize) this.allLoaded = true;
        this.offset += this.pageSize;
        this.updateUnreadCount();

        // Set latest notification for preview if there are unread notifications
        const unreadNotifications = this.notifications.filter(n => !n.read);
        if (unreadNotifications.length > 0 && !this.latestNotification) {
          this.latestNotification = unreadNotifications[0];
        }

        this.loadingMore = false;
      },
      error: (error) => {
        this.loadingMore = false;
      }
    });
  }

  onNotificationScroll(event: any): void {
    const el = event.target;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
      this.loadNotificationsPage();
    }
  }

  private subscribeToNotifications(): void {
    // Subscribe to individual WebSocket notifications
    this.webSocketService.getIndividualNotifications()
      .pipe(takeUntil(this.destroy$))
      .subscribe(notification => {

        if (notification) {
          // Filter notifications based on user role
          const currentUser = this.authService.getUser();
          const isAdmin = currentUser?.role === 'ADMIN';

          // Skip admin notifications for non-admin users
          if (!isAdmin && notification.type && (
            notification.type === 'CASH_DISCREPANCY_PENDING' ||
            notification.type === 'STOCK_DISCREPANCY_PENDING' ||
            notification.type === 'NEW_USER_REGISTRATION' ||
            notification.type === 'EMAIL_VERIFIED' ||
            notification.type === 'QUANTITY_REDUCTION_ATTEMPT'
          )) {
            return; // Skip this notification for non-admin users
          }

          const processedTimestamp = this.parseTimestamp(notification.timestamp);

          // Parse notification data if it's a string
          let processedData = notification.data;
          if (typeof notification.data === 'string' && notification.data) {
            try {
              processedData = JSON.parse(notification.data);
            } catch (error) {
              processedData = null;
            }
          }

          const processedNotification = {
            ...notification,
            timestamp: processedTimestamp,
            data: processedData,
            read: false // Always mark new notifications as unread
          };

          // Check if this notification already exists
          const existingIndex = this.notifications.findIndex(n => n.id == notification.id);

          if (existingIndex >= 0) {
            // Update existing notification with WebSocket data
            this.notifications[existingIndex] = {
              ...this.notifications[existingIndex],
              ...processedNotification,
              // Preserve read status from existing notification if it was already read
              read: this.notifications[existingIndex].read || processedNotification.read
            };
            // ❌ DON'T show toast for duplicate/updated notifications
            console.log('📝 [NAV] Notification updated (not shown again):', notification.id);
          } else {
            // Add new notification to the beginning of the list
            this.notifications.unshift(processedNotification);

            // ✅ ONLY show toast for NEW notifications
            this.showNotificationToast(processedNotification);

            // Set flag for new notification styling
            this.hasNewNotification = true;

            // Set the latest notification for preview
            this.latestNotification = processedNotification;

            // Show temporary notification message
            this.showNotificationMessage = true;
            setTimeout(() => {
              this.showNotificationMessage = false;
            }, 3000); // Hide message after 3 seconds

            setTimeout(() => {
              this.hasNewNotification = false;
            }, 5000); // Remove enhanced styling after 5 seconds
          }

          // Sort by timestamp (newest first) to maintain proper order
          this.notifications.sort((a, b) => {
            const dateA = new Date(a.timestamp);
            const dateB = new Date(b.timestamp);
            return dateB.getTime() - dateA.getTime();
          });

          this.updateUnreadCount();

          // Force change detection to update the UI immediately
          this.cdr.detectChanges();
        }
      });
  }

  private updateUnreadCount(): void {
    this.unreadCount = this.notifications.filter(n => !n.read).length;
  }

  private startTimestampRefreshTimer(): void {
    // Refresh timestamps every 30 seconds to keep them up to date
    this.timestampRefreshTimer = setInterval(() => {
      // Force change detection to update timestamps
      this.cdr.detectChanges();
    }, 30000); // 30 seconds
  }



  private showNotificationToast(notification: Notification): void {
    let message = '';

    switch (notification.type) {
      case 'NEW_USER_REGISTRATION':
        message = 'Nouvel utilisateur inscrit - En attente de vérification email';
        this.notificationService.info(message);
        break;
      case 'EMAIL_VERIFIED':
        message = 'Email vérifié - En attente d\'approbation admin';
        this.notificationService.success(message);
        break;
      case 'USER_APPROVED':
        message = 'Utilisateur approuvé avec succès';
        this.notificationService.success(message);
        break;
      case 'USER_REJECTED':
        message = 'Utilisateur rejeté';
        this.notificationService.warning(message);
        break;
      case 'CAMERA_VERIFICATION_URGENT':
        // 🚨 CRITICAL: toast handled here, native notification handled by app.component.ts
        this.notificationService.error(notification.message);
        break;
      case 'RECONCILIATION_SUCCESS':
        this.notificationService.success(notification.message);
        this.notificationService.showNativeNotification(notification.message, notification.type);
        break;
      case 'RECONCILIATION_WARNING':
        this.notificationService.warning(notification.message);
        this.notificationService.showNativeNotification(notification.message, notification.type);
        break;
      case 'RECONCILIATION_DANGER':
        this.notificationService.error(notification.message);
        this.notificationService.showNativeNotification(notification.message, notification.type);
        break;
      default:
        message = notification.message;
        this.notificationService.info(message);
    }
  }

  markAsRead(notificationId: string, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    // Find notification in the list
    const notif = this.notifications.find(n => n.id == notificationId);
    if (notif) {
      if (!notif.read) {
        // Update local state immediately for better UX
        notif.read = true;
        this.updateUnreadCount();

        // Clear latest notification if it's the one being marked as read
        if (this.latestNotification && this.latestNotification.id == notificationId) {
          this.latestNotification = null;
        }

        // Force change detection to update UI
        this.cdr.detectChanges();

        // Validate notification ID before converting to number
        let numericId: number;

        if (typeof notificationId === 'string') {
          // Check if it's a valid numeric string
          if (/^\d+$/.test(notificationId)) {
            numericId = parseInt(notificationId, 10);
          } else {
            this.notificationService.error('ID de notification invalide');
            // Revert local state
            notif.read = false;
            this.updateUnreadCount();
            this.cdr.detectChanges();
            return;
          }
        } else if (typeof notificationId === 'number') {
          numericId = notificationId;
        } else {
          this.notificationService.error('ID de notification invalide');
          // Revert local state
          notif.read = false;
          this.updateUnreadCount();
          this.cdr.detectChanges();
          return;
        }

        if (isNaN(numericId)) {
          this.notificationService.error('ID de notification invalide');
          // Revert local state
          notif.read = false;
          this.updateUnreadCount();
          this.cdr.detectChanges();
          return;
        }

        // Persist to backend
        this.notificationService.markAsRead(numericId).subscribe({
          next: () => {
            // Update WebSocket to sync with other clients
            this.webSocketService.markAsRead(notificationId);
          },
          error: (error) => {
            // For any error, revert local state to maintain consistency
            notif.read = false;
            this.updateUnreadCount();
            this.cdr.detectChanges();

            // Show user-friendly error message
            if (error.status === 403) {
              this.notificationService.error('Vous n\'êtes pas autorisé à marquer cette notification comme lue');
            } else if (error.status === 404) {
              this.notificationService.error('Notification introuvable');
            } else {
              this.notificationService.error('Erreur lors du marquage de la notification');
            }
          }
        });
      }
    }
  }

  markAllAsRead(): void {
    this.webSocketService.markAllAsRead();
  }

  clearNotifications(): void {
    this.webSocketService.clearNotifications();
  }

  formatNotificationDate(timestamp: any): string {
    try {
      if (!timestamp) {
        return 'Date invalide';
      }
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        return 'Date invalide';
      }
      const formatted = date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      return formatted;
    } catch (error) {
      return 'Date invalide';
    }
  }

  getRelativeTime(timestamp: any): string {
    try {
      const date = this.parseTimestamp(timestamp);
      const now = new Date();
      const diffInMs = now.getTime() - date.getTime();

      // Handle future dates (shouldn't happen but just in case)
      if (diffInMs < 0) {
        return 'À l\'instant';
      }

      const diffInSeconds = Math.floor(diffInMs / 1000);
      const diffInMinutes = Math.floor(diffInSeconds / 60);
      const diffInHours = Math.floor(diffInMinutes / 60);
      const diffInDays = Math.floor(diffInHours / 24);

      if (diffInSeconds < 30) {
        return 'À l\'instant';
      } else if (diffInSeconds < 60) {
        return `${diffInSeconds}s`;
      } else if (diffInMinutes < 60) {
        return `${diffInMinutes}m`;
      } else if (diffInHours < 24) {
        return `${diffInHours}h`;
      } else if (diffInDays < 7) {
        return `${diffInDays}j`;
      } else {
        const weeks = Math.floor(diffInDays / 7);
        return `${weeks}s`;
      }
    } catch (error) {
      return 'À l\'instant';
    }
  }

  approveUser(notificationId: string, role: string, event: MouseEvent): void {
    event.stopPropagation();


    // Add loading state
    this.approvingNotifications.add(notificationId);

    // Find the notification to get the data
    const notification = this.notifications.find(n => n.id == notificationId);
    if (!notification) {
      console.error('❌ [NAV-BAR] Notification not found:', notificationId);
      this.notificationService.error('Notification introuvable');
      this.approvingNotifications.delete(notificationId);
      return;
    }

    // Extract userId from notification data
    let userId: number | null = null;
    try {
      if (notification.data) {
        let data: any;
        // Handle different data formats
        if (typeof notification.data === 'string') {
          // Check if it's the old Java toString() format: {key=value, key2=value2}
          if (notification.data.startsWith('{') && notification.data.includes('=')) {
            // Parse Java toString() format: {userId=45, selectedRole=USER, email=aourassh@gmail.com}
            const cleanData = notification.data.substring(1, notification.data.length - 1); // Remove { and }
            const pairs = cleanData.split(', ');
            data = {};
            pairs.forEach(pair => {
              const [key, value] = pair.split('=');
              data[key.trim()] = value.trim();
            });
          } else {
            // Try to parse as JSON
            data = JSON.parse(notification.data);
          }
        } else if (typeof notification.data === 'object') {
          // Data is already an object from WebSocket
          data = notification.data;
        } else {
          this.approvingNotifications.delete(notificationId);
          return;
        }
        userId = parseInt(data.userId, 10);
      }
    } catch (error) {
      this.approvingNotifications.delete(notificationId);
      return;
    }

    if (!userId) {
      this.notificationService.error('Impossible d\'extraire l\'ID utilisateur');
      this.approvingNotifications.delete(notificationId);
      return;
    }



    // Call the approval service with the selected role
    this.authService.approveUser(userId, role).subscribe({
      next: (response) => {


        // Update the notification message to show it was approved
        notification.message = 'Email vérifié et utilisateur approuvé avec succès';
        notification.type = 'USER_APPROVED';
        notification.read = true;

        // Mark the notification as read in the backend
        let numericId: number;

        if (typeof notificationId === 'string') {
          // Check if it's a valid numeric string
          if (/^\d+$/.test(notificationId)) {
            numericId = parseInt(notificationId, 10);
          } else {
            console.error('❌ [NAV-BAR] Invalid notification ID format for markAsRead:', notificationId);
            return;
          }
        } else if (typeof notificationId === 'number') {
          numericId = notificationId;
        } else {
          console.error('❌ [NAV-BAR] Invalid notification ID type for markAsRead:', typeof notificationId, notificationId);
          return;
        }

        if (!isNaN(numericId)) {
          this.notificationService.markAsRead(numericId).subscribe({
            next: () => {
              console.log('✅ [NAV-BAR] Notification marked as read on backend');
              // Update WebSocket to sync with other clients
              this.webSocketService.markAsRead(notificationId);
            },
            error: (error) => {
              console.error('❌ [NAV-BAR] Error marking notification as read:', error);
            }
          });
        } else {
          console.error('❌ [NAV-BAR] Invalid notification ID for markAsRead:', notificationId);
        }

        // Update unread count
        this.updateUnreadCount();

        // Show success message
        this.notificationService.success('Utilisateur approuvé avec succès');

        // Remove loading state
        this.approvingNotifications.delete(notificationId);

        // Force change detection
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.notificationService.error('Erreur lors de l\'approbation: ' + (error.message || 'Erreur inconnue'));

        // Remove loading state
        this.approvingNotifications.delete(notificationId);

        // Force change detection
        this.cdr.detectChanges();
      }
    });
  }

  getSelectedRole(notification: Notification): string {
    try {
      if (notification.data) {
        let data: any;

        // Handle different data formats
        if (typeof notification.data === 'string') {
          // Check if it's the old Java toString() format: {key=value, key2=value2}
          if (notification.data.startsWith('{') && notification.data.includes('=')) {
            // Parse Java toString() format: {userId=45, selectedRole=USER, email=aourassh@gmail.com}
            const cleanData = notification.data.substring(1, notification.data.length - 1); // Remove { and }
            const pairs = cleanData.split(', ');
            data = {};
            pairs.forEach(pair => {
              const [key, value] = pair.split('=');
              data[key.trim()] = value.trim();
            });
          } else {
            // Try to parse as JSON
            data = JSON.parse(notification.data);
          }
        } else if (typeof notification.data === 'object') {
          // Data is already an object from WebSocket
          data = notification.data;
        } else {
          return 'USER';
        }


        return data.selectedRole || 'USER';
      }
    } catch (error) {
      // Return default role on error
    }
    return 'USER'; // Default fallback
  }

  isNotificationProcessing(notificationId: string): boolean {
    return this.approvingNotifications.has(notificationId) || this.rejectingNotifications.has(notificationId);
  }

  private parseTimestamp(timestamp: any): Date {
    if (!timestamp) {
      return new Date();
    }

    let date: Date;

    if (timestamp instanceof Date) {
      date = timestamp;
    } else if (Array.isArray(timestamp)) {
      // Handle array format [year, month, day, hour, minute, second]
      // Note: Month is 0-indexed in JS Date, but typically 1-indexed in Java/JSON
      if (timestamp.length >= 3) {
        const year = timestamp[0];
        const month = timestamp[1] - 1; // Convert 1-based month to 0-based
        const day = timestamp[2];
        const hour = timestamp[3] || 0;
        const minute = timestamp[4] || 0;
        const second = timestamp[5] || 0;
        date = new Date(year, month, day, hour, minute, second);
      } else {
        date = new Date();
      }
    } else if (typeof timestamp === 'string') {
      // Handle different string formats
      if (timestamp.includes('T')) {
        // ISO format: "2024-01-15T10:30:00" or "2024-01-15T10:30:00.000Z"
        date = new Date(timestamp);
      } else if (timestamp.includes('-') && timestamp.includes(':')) {
        // Date format: "2024-01-15 10:30:00"
        date = new Date(timestamp.replace(' ', 'T'));
      } else if (timestamp.includes('-')) {
        // Date only format: "2024-01-15"
        date = new Date(timestamp + 'T00:00:00');
      } else if (/^\d+$/.test(timestamp)) {
        // Unix timestamp (seconds or milliseconds)
        const num = parseInt(timestamp, 10);
        // If it's less than 10000000000, it's likely seconds, otherwise milliseconds
        date = new Date(num < 10000000000 ? num * 1000 : num);
      } else {
        // Try parsing as is
        date = new Date(timestamp);
      }
    } else if (typeof timestamp === 'number') {
      // If it's less than 10000000000, it's likely seconds, otherwise milliseconds
      date = new Date(timestamp < 10000000000 ? timestamp * 1000 : timestamp);
    } else {
      // Try to convert to string and parse
      date = new Date(String(timestamp));
    }

    // Validate the parsed date
    if (isNaN(date.getTime())) {
      console.warn('❌ [NAV-BAR] Invalid date parsed:', timestamp);
      return new Date();
    }

    return date;
  }

  rejectUser(notificationId: string, event: MouseEvent): void {
    event.stopPropagation();


    // Add loading state
    this.rejectingNotifications.add(notificationId);

    // Find the notification to get the data
    const notification = this.notifications.find(n => n.id == notificationId);
    if (!notification) {
      console.error('❌ [NAV-BAR] Notification not found:', notificationId);
      this.notificationService.error('Notification introuvable');
      this.rejectingNotifications.delete(notificationId);
      return;
    }

    // Extract userId from notification data
    let userId: number | null = null;
    try {
      if (notification.data) {
        let data: any;
        // Handle different data formats
        if (typeof notification.data === 'string') {
          // Check if it's the old Java toString() format: {key=value, key2=value2}
          if (notification.data.startsWith('{') && notification.data.includes('=')) {
            // Parse Java toString() format: {userId=45, selectedRole=USER, email=aourassh@gmail.com}
            const cleanData = notification.data.substring(1, notification.data.length - 1); // Remove { and }
            const pairs = cleanData.split(', ');
            data = {};
            pairs.forEach(pair => {
              const [key, value] = pair.split('=');
              data[key.trim()] = value.trim();
            });
          } else {
            // Try to parse as JSON
            data = JSON.parse(notification.data);
          }
        } else if (typeof notification.data === 'object') {
          // Data is already an object from WebSocket
          data = notification.data;
        } else {
          console.warn('❌ [NAV-BAR] Unknown notification data format:', typeof notification.data);
          this.rejectingNotifications.delete(notificationId);
          return;
        }
        userId = parseInt(data.userId, 10);
      }
    } catch (error) {
      console.error('❌ [NAV-BAR] Error parsing notification data:', error);
      console.error('❌ [NAV-BAR] Raw notification data:', notification.data);
      this.rejectingNotifications.delete(notificationId);
      return;
    }

    if (!userId) {
      console.error('❌ [NAV-BAR] Could not extract userId from notification data');
      this.notificationService.error('Impossible d\'extraire l\'ID utilisateur');
      this.rejectingNotifications.delete(notificationId);
      return;
    }



    // Call the rejection service
    this.authService.rejectUser(userId).subscribe({
      next: (response) => {


        // Update the notification message to show it was rejected
        notification.message = 'Email vérifié mais utilisateur rejeté';
        notification.type = 'USER_REJECTED';
        notification.read = true;

        // Mark the notification as read in the backend
        let numericId: number;

        if (typeof notificationId === 'string') {
          // Check if it's a valid numeric string
          if (/^\d+$/.test(notificationId)) {
            numericId = parseInt(notificationId, 10);
          } else {
            console.error('❌ [NAV-BAR] Invalid notification ID format for markAsRead:', notificationId);
            return;
          }
        } else if (typeof notificationId === 'number') {
          numericId = notificationId;
        } else {
          console.error('❌ [NAV-BAR] Invalid notification ID type for markAsRead:', typeof notificationId, notificationId);
          return;
        }

        if (!isNaN(numericId)) {
          this.notificationService.markAsRead(numericId).subscribe({
            next: () => {
              console.log('✅ [NAV-BAR] Notification marked as read on backend');
              // Update WebSocket to sync with other clients
              this.webSocketService.markAsRead(notificationId);
            },
            error: (error) => {
              console.error('❌ [NAV-BAR] Error marking notification as read:', error);
            }
          });
        } else {
          console.error('❌ [NAV-BAR] Invalid notification ID for markAsRead:', notificationId);
        }

        // Update unread count
        this.updateUnreadCount();

        // Show warning message
        this.notificationService.warning('Utilisateur rejeté');

        // Remove loading state
        this.rejectingNotifications.delete(notificationId);

        // Force change detection
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('❌ [NAV-BAR] Error rejecting user:', error);
        this.notificationService.error('Erreur lors du rejet: ' + (error.message || 'Erreur inconnue'));

        // Remove loading state
        this.rejectingNotifications.delete(notificationId);

        // Force change detection
        this.cdr.detectChanges();
      }
    });
  }

  // Méthodes pour les écarts de caisse
  approveCashDiscrepancy(notificationId: string, event: MouseEvent): void {
    // Add loading state
    this.approvingNotifications.add(notificationId);

    // Find the notification to get the data
    const notification = this.notifications.find(n => n.id == notificationId);
    if (!notification) {
      console.error('❌ [NAV-BAR] Notification not found:', notificationId);
      this.notificationService.error('Notification introuvable');
      this.approvingNotifications.delete(notificationId);
      return;
    }

    // Extract discrepancyId from notification data
    let discrepancyId: number | null = null;
    try {
      if (notification.data) {
        let data: any;
        // Handle different data formats
        if (typeof notification.data === 'string') {
          // Check if it's the old Java toString() format: {key=value, key2=value2}
          if (notification.data.startsWith('{') && notification.data.includes('=')) {
            // Parse Java toString() format: {discrepancyId=36, realAmount=6654, systemAmount=6500}
            const cleanData = notification.data.substring(1, notification.data.length - 1); // Remove { and }
            const pairs = cleanData.split(', ');
            data = {};
            pairs.forEach(pair => {
              const [key, value] = pair.split('=');
              data[key.trim()] = value.trim();
            });
          } else {
            // Try to parse as JSON
            data = JSON.parse(notification.data);
          }
        } else if (typeof notification.data === 'object') {
          // Data is already an object from WebSocket
          data = notification.data;
        } else {
          console.warn('❌ [NAV-BAR] Unknown notification data format:', typeof notification.data);
          this.approvingNotifications.delete(notificationId);
          return;
        }

        discrepancyId = parseInt(data.discrepancyId, 10);
      }
    } catch (error) {
      console.error('❌ [NAV-BAR] Error parsing notification data:', error);
      this.approvingNotifications.delete(notificationId);
      return;
    }

    if (!discrepancyId || isNaN(discrepancyId)) {
      console.error('❌ [NAV-BAR] No valid discrepancyId found in notification data');
      this.notificationService.error('ID d\'écart introuvable ou invalide');
      this.approvingNotifications.delete(notificationId);
      return;
    }

    // Call the cash discrepancy service
    this.cashDiscrepancyService.approveCashDiscrepancy(discrepancyId).subscribe({
      next: (response) => {
        this.notificationService.success('Écart de caisse approuvé avec succès');

        // Update notification status instead of removing it
        const notificationIndex = this.notifications.findIndex(n => n.id == notificationId);
        if (notificationIndex !== -1) {
          this.notifications[notificationIndex] = {
            ...this.notifications[notificationIndex],
            type: 'CASH_DISCREPANCY_APPROVED',
            message: 'Écart de caisse approuvé avec succès',
            read: true
          };
        }
        this.updateUnreadCount();

        // Mark the notification as read in the backend
        let numericId: number;
        if (typeof notificationId === 'string') {
          if (/^\d+$/.test(notificationId)) {
            numericId = parseInt(notificationId, 10);
          } else {
            console.error('❌ [NAV-BAR] Invalid notification ID format:', notificationId);
            return;
          }
        } else if (typeof notificationId === 'number') {
          numericId = notificationId;
        } else {
          console.error('❌ [NAV-BAR] Invalid notification ID type:', typeof notificationId, notificationId);
          return;
        }

        if (!isNaN(numericId)) {
          this.notificationService.markAsRead(numericId).subscribe({
            next: () => {
              // Update WebSocket to sync with other clients
              this.webSocketService.markAsRead(notificationId);

              // Refresh notifications to get updated status from backend
              setTimeout(() => {
                this.loadNotificationsPage(true);
              }, 500);
            },
            error: (error) => {
              console.error('❌ [NAV-BAR] Error marking notification as read:', error);
            }
          });
        }

        // Remove loading state
        this.approvingNotifications.delete(notificationId);

        // Force change detection
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('❌ [NAV-BAR] Error approving cash discrepancy:', error);
        this.notificationService.error('Erreur lors de l\'approbation de l\'écart de caisse');
        this.approvingNotifications.delete(notificationId);
      }
    });
  }

  rejectCashDiscrepancy(notificationId: string, event: MouseEvent): void {
    // Add loading state
    this.rejectingNotifications.add(notificationId);

    // Find the notification to get the data
    const notification = this.notifications.find(n => n.id == notificationId);
    if (!notification) {
      console.error('❌ [NAV-BAR] Notification not found:', notificationId);
      this.notificationService.error('Notification introuvable');
      this.rejectingNotifications.delete(notificationId);
      return;
    }

    // Extract discrepancyId from notification data
    let discrepancyId: number | null = null;
    try {
      if (notification.data) {
        let data: any;
        // Handle different data formats
        if (typeof notification.data === 'string') {
          // Check if it's the old Java toString() format: {key=value, key2=value2}
          if (notification.data.startsWith('{') && notification.data.includes('=')) {
            // Parse Java toString() format: {discrepancyId=36, realAmount=6654, systemAmount=6500}
            const cleanData = notification.data.substring(1, notification.data.length - 1); // Remove { and }
            const pairs = cleanData.split(', ');
            data = {};
            pairs.forEach(pair => {
              const [key, value] = pair.split('=');
              data[key.trim()] = value.trim();
            });
          } else {
            // Try to parse as JSON
            data = JSON.parse(notification.data);
          }
        } else if (typeof notification.data === 'object') {
          // Data is already an object from WebSocket
          data = notification.data;
        } else {
          console.warn('❌ [NAV-BAR] Unknown notification data format:', typeof notification.data);
          this.rejectingNotifications.delete(notificationId);
          return;
        }

        discrepancyId = parseInt(data.discrepancyId, 10);
      }
    } catch (error) {
      console.error('❌ [NAV-BAR] Error parsing notification data:', error);
      this.rejectingNotifications.delete(notificationId);
      return;
    }

    if (!discrepancyId || isNaN(discrepancyId)) {
      console.error('❌ [NAV-BAR] No valid discrepancyId found in notification data');
      this.notificationService.error('ID d\'écart introuvable ou invalide');
      this.rejectingNotifications.delete(notificationId);
      return;
    }

    // Call the cash discrepancy service
    this.cashDiscrepancyService.rejectCashDiscrepancy(discrepancyId).subscribe({
      next: (response) => {
        this.notificationService.success('Écart de caisse rejeté avec succès');

        // Update notification status instead of removing it
        const notificationIndex = this.notifications.findIndex(n => n.id == notificationId);
        if (notificationIndex !== -1) {
          this.notifications[notificationIndex] = {
            ...this.notifications[notificationIndex],
            type: 'CASH_DISCREPANCY_REJECTED',
            message: 'Écart de caisse rejeté',
            read: true
          };
        }
        this.updateUnreadCount();

        // Mark the notification as read in the backend
        let numericId: number;
        if (typeof notificationId === 'string') {
          if (/^\d+$/.test(notificationId)) {
            numericId = parseInt(notificationId, 10);
          } else {
            console.error('❌ [NAV-BAR] Invalid notification ID format:', notificationId);
            return;
          }
        } else if (typeof notificationId === 'number') {
          numericId = notificationId;
        } else {
          console.error('❌ [NAV-BAR] Invalid notification ID type:', typeof notificationId, notificationId);
          return;
        }

        if (!isNaN(numericId)) {
          this.notificationService.markAsRead(numericId).subscribe({
            next: () => {
              // Update WebSocket to sync with other clients
              this.webSocketService.markAsRead(notificationId);

              // Refresh notifications to get updated status from backend
              setTimeout(() => {
                this.loadNotificationsPage(true);
              }, 500);
            },
            error: (error) => {
              console.error('❌ [NAV-BAR] Error marking notification as read:', error);
            }
          });
        }

        // Remove loading state
        this.rejectingNotifications.delete(notificationId);

        // Force change detection
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('❌ [NAV-BAR] Error rejecting cash discrepancy:', error);
        this.notificationService.error('Erreur lors du rejet de l\'écart de caisse');
        this.rejectingNotifications.delete(notificationId);
      }
    });
  }

  // Méthodes pour les écarts de stock
  approveStockDiscrepancy(notificationId: string, event: MouseEvent): void {
    // Add loading state
    this.approvingNotifications.add(notificationId);

    // Find the notification to get the data
    const notification = this.notifications.find(n => n.id == notificationId);
    if (!notification) {
      console.error('❌ [NAV-BAR] Notification not found:', notificationId);
      this.notificationService.error('Notification introuvable');
      this.approvingNotifications.delete(notificationId);
      return;
    }

    // Extract discrepancyId from notification data
    let discrepancyId: number | null = null;
    try {
      if (notification.data) {
        let data: any;
        // Handle different data formats
        if (typeof notification.data === 'string') {
          // Check if it's the old Java toString() format: {key=value, key2=value2}
          if (notification.data.startsWith('{') && notification.data.includes('=')) {
            // Parse Java toString() format: {discrepancyId=36, realAmount=6654, systemAmount=6500}
            const cleanData = notification.data.substring(1, notification.data.length - 1); // Remove { and }
            const pairs = cleanData.split(', ');
            data = {};
            pairs.forEach(pair => {
              const [key, value] = pair.split('=');
              data[key.trim()] = value.trim();
            });
          } else {
            // Try to parse as JSON
            data = JSON.parse(notification.data);
          }
        } else if (typeof notification.data === 'object') {
          // Data is already an object from WebSocket
          data = notification.data;
        } else {
          console.warn('❌ [NAV-BAR] Unknown notification data format:', typeof notification.data);
          this.approvingNotifications.delete(notificationId);
          return;
        }

        discrepancyId = parseInt(data.discrepancyId, 10);
      }
    } catch (error) {
      console.error('❌ [NAV-BAR] Error parsing notification data:', error);
      this.approvingNotifications.delete(notificationId);
      return;
    }

    if (!discrepancyId || isNaN(discrepancyId)) {
      console.error('❌ [NAV-BAR] No valid discrepancyId found in notification data');
      this.notificationService.error('ID d\'écart introuvable ou invalide');
      this.approvingNotifications.delete(notificationId);
      return;
    }

    // Call the stock discrepancy service
    this.stockDiscrepancyService.approveStockDiscrepancy(discrepancyId).subscribe({
      next: (response) => {
        this.notificationService.success('Écart de stock approuvé avec succès');

        // Update notification status instead of removing it
        const notificationIndex = this.notifications.findIndex(n => n.id == notificationId);
        if (notificationIndex !== -1) {
          this.notifications[notificationIndex] = {
            ...this.notifications[notificationIndex],
            type: 'STOCK_DISCREPANCY_APPROVED',
            message: 'Écart de stock approuvé avec succès',
            read: true
          };
        }
        this.updateUnreadCount();

        // Mark the notification as read in the backend
        let numericId: number;
        if (typeof notificationId === 'string') {
          if (/^\d+$/.test(notificationId)) {
            numericId = parseInt(notificationId, 10);
          } else {
            console.error('❌ [NAV-BAR] Invalid notification ID format:', notificationId);
            return;
          }
        } else if (typeof notificationId === 'number') {
          numericId = notificationId;
        } else {
          console.error('❌ [NAV-BAR] Invalid notification ID type:', typeof notificationId, notificationId);
          return;
        }

        if (!isNaN(numericId)) {
          this.notificationService.markAsRead(numericId).subscribe({
            next: () => {
              // Update WebSocket to sync with other clients
              this.webSocketService.markAsRead(notificationId);

              // Refresh notifications to get updated status from backend
              setTimeout(() => {
                this.loadNotificationsPage(true);
              }, 500);
            },
            error: (error) => {
              console.error('❌ [NAV-BAR] Error marking notification as read:', error);
            }
          });
        }

        // Remove loading state
        this.approvingNotifications.delete(notificationId);

        // Force change detection
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('❌ [NAV-BAR] Error approving stock discrepancy:', error);
        this.notificationService.error('Erreur lors de l\'approbation de l\'écart de stock');
        this.approvingNotifications.delete(notificationId);
      }
    });
  }

  rejectStockDiscrepancy(notificationId: string, event: MouseEvent): void {
    // Add loading state
    this.rejectingNotifications.add(notificationId);

    // Find the notification to get the data
    const notification = this.notifications.find(n => n.id == notificationId);
    if (!notification) {
      console.error('❌ [NAV-BAR] Notification not found:', notificationId);
      this.notificationService.error('Notification introuvable');
      this.rejectingNotifications.delete(notificationId);
      return;
    }

    // Extract discrepancyId from notification data
    let discrepancyId: number | null = null;
    try {
      if (notification.data) {
        let data: any;
        // Handle different data formats
        if (typeof notification.data === 'string') {
          // Check if it's the old Java toString() format: {key=value, key2=value2}
          if (notification.data.startsWith('{') && notification.data.includes('=')) {
            // Parse Java toString() format: {discrepancyId=36, realAmount=6654, systemAmount=6500}
            const cleanData = notification.data.substring(1, notification.data.length - 1); // Remove { and }
            const pairs = cleanData.split(', ');
            data = {};
            pairs.forEach(pair => {
              const [key, value] = pair.split('=');
              data[key.trim()] = value.trim();
            });
          } else {
            // Try to parse as JSON
            data = JSON.parse(notification.data);
          }
        } else if (typeof notification.data === 'object') {
          // Data is already an object from WebSocket
          data = notification.data;
        } else {
          console.warn('❌ [NAV-BAR] Unknown notification data format:', typeof notification.data);
          this.rejectingNotifications.delete(notificationId);
          return;
        }

        discrepancyId = parseInt(data.discrepancyId, 10);
      }
    } catch (error) {
      console.error('❌ [NAV-BAR] Error parsing notification data:', error);
      this.rejectingNotifications.delete(notificationId);
      return;
    }

    if (!discrepancyId || isNaN(discrepancyId)) {
      console.error('❌ [NAV-BAR] No valid discrepancyId found in notification data');
      this.notificationService.error('ID d\'écart introuvable ou invalide');
      this.rejectingNotifications.delete(notificationId);
      return;
    }

    // Call the stock discrepancy service
    this.stockDiscrepancyService.rejectStockDiscrepancy(discrepancyId).subscribe({
      next: (response) => {
        this.notificationService.success('Écart de stock rejeté avec succès');

        // Update notification status instead of removing it
        const notificationIndex = this.notifications.findIndex(n => n.id == notificationId);
        if (notificationIndex !== -1) {
          this.notifications[notificationIndex] = {
            ...this.notifications[notificationIndex],
            type: 'STOCK_DISCREPANCY_REJECTED',
            message: 'Écart de stock rejeté',
            read: true
          };
        }
        this.updateUnreadCount();

        // Mark the notification as read in the backend
        let numericId: number;
        if (typeof notificationId === 'string') {
          if (/^\d+$/.test(notificationId)) {
            numericId = parseInt(notificationId, 10);
          } else {
            console.error('❌ [NAV-BAR] Invalid notification ID format:', notificationId);
            return;
          }
        } else if (typeof notificationId === 'number') {
          numericId = notificationId;
        } else {
          console.error('❌ [NAV-BAR] Invalid notification ID type:', typeof notificationId, notificationId);
          return;
        }

        if (!isNaN(numericId)) {
          this.notificationService.markAsRead(numericId).subscribe({
            next: () => {
              // Update WebSocket to sync with other clients
              this.webSocketService.markAsRead(notificationId);

              // Refresh notifications to get updated status from backend
              setTimeout(() => {
                this.loadNotificationsPage(true);
              }, 500);
            },
            error: (error) => {
              console.error('❌ [NAV-BAR] Error marking notification as read:', error);
            }
          });
        }

        // Remove loading state
        this.rejectingNotifications.delete(notificationId);

        // Force change detection
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('❌ [NAV-BAR] Error rejecting stock discrepancy:', error);
        this.notificationService.error('Erreur lors du rejet de l\'écart de stock');
        this.rejectingNotifications.delete(notificationId);
      }
    });
  }

  approveAvoirRequest(notificationId: string, event: MouseEvent): void {
    event.stopPropagation();
    this.approvingNotifications.add(notificationId);

    const notification = this.notifications.find(n => n.id == notificationId);
    if (!notification || !notification.data) {
      this.approvingNotifications.delete(notificationId);
      return;
    }

    // Extract avoirId
    let avoirId: number;
    try {
      const data = typeof notification.data === 'string' ? JSON.parse(notification.data) : notification.data;
      avoirId = data.avoirId;
    } catch (e) {
      this.approvingNotifications.delete(notificationId);
      return;
    }

    this.avoirService.approveAvoir(avoirId).subscribe({
      next: (res) => {
        this.notificationService.success('Avoir approuvé avec succès. Le cash a été déduit.');
        notification.read = true;
        notification.type = 'APPROVED_AVOIR'; // Change type to hide buttons
        
        // Mark notification as read
        const numericId = typeof notificationId === 'string' ? parseInt(notificationId, 10) : notificationId;
        this.notificationService.markAsRead(numericId).subscribe(() => {
          this.webSocketService.markAsRead(notificationId);
          this.updateUnreadCount();
        });

        this.approvingNotifications.delete(notificationId);
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.notificationService.error('Erreur lors de l\'approbation: ' + (err.error?.message || 'Inconnue'));
        this.approvingNotifications.delete(notificationId);
        this.cdr.detectChanges();
      }
    });
  }

  getWindowSize(): { width: number; height: number; isMobile: boolean } {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const isMobile = width < 1536; // 2xl breakpoint is 1536px

    return { width, height, isMobile };
  }

  handleWindowResize(): void {
    const { width, isMobile } = this.getWindowSize();

    // Close mobile menu when switching to desktop (2xl breakpoint)
    if (!isMobile && this.isMobileMenuOpen) {
      this.closeMobileMenu();
    }

    // Close dropdowns when resizing
    if (this.activeDropdown) {
      this.closeDropdown();
    }

    // Force change detection
    this.cdr.detectChanges();
  }



  // Method to refresh notifications (called when discrepancy is approved from discrepancy page)
  refreshNotifications(): void {
    this.loadNotificationsPage(true);
  }


  // Method to connect cash drawer
  connectCashDrawer(): void {
    this.hardwareService.openDrawer(CashOpenOrigin.BUTTON);
  }

  connectScanner(): void {
    this.barcodeScannerService.connectScanner().then(ok => {
      if (ok) {
        this.notificationService.success('Scanner connecté (mode HID)');
      } else {
        this.notificationService.error('Connexion scanner échouée — vérifiez que le scanner est branché et autorisé');
      }
    });
  }
}

